import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Trash2, RotateCcw, MapPin, CheckCircle2, AlertTriangle, Globe2,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Sub, Skeleton, IconBtn } from "./_shared";

export default function FacilitiesAdminSection() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "unverified", source: "", country: "", q: "" });
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: filters.status, limit: "100" });
      if (filters.source) params.set("source", filters.source);
      if (filters.country) params.set("country", filters.country);
      if (filters.q) params.set("q", filters.q);
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/admin/facilities?${params.toString()}`),
        api.get("/admin/facilities/stats"),
      ]);
      setRows(list.results || []);
      setTotal(list.total || (list.results || []).length);
      setStats(s);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load facilities"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { refresh(); }, [refresh]);

  const patch = async (fid, body, label) => {
    try { await api.patch(`/admin/facilities/${fid}`, body); toast.success(label || "Updated"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const del = async (fid, name) => {
    if (!window.confirm(`Delete "${name}"? Cannot be undone.`)) return;
    try { await api.delete(`/admin/facilities/${fid}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const reimportRegions = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/facilities/import-regions", {});
      toast.success(`Imported ${data.saved} new venues across ${data.regions} regions`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setImporting(false); }
  };

  const toggleRow = (fid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return next;
    });
  };
  const togglePageAll = () => {
    if (selected.size === rows.length && rows.length > 0) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const bulkVerifyIds = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Verify ${selected.size} selected ${selected.size === 1 ? "facility" : "facilities"}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/facilities/bulk-verify", { ids: Array.from(selected) });
      toast.success(`Verified ${data.modified} / ${data.matched}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBulkBusy(false); }
  };
  const bulkVerifyAllMatching = async () => {
    if (!window.confirm(`Verify ALL ${total} facilities matching the current filters? This flips them to verified and makes them available to clubs.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/facilities/bulk-verify", {
        all: true,
        status: filters.status,
        source: filters.source || undefined,
        country: filters.country || undefined,
        q: filters.q || undefined,
      });
      toast.success(`Verified ${data.modified} / ${data.matched}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBulkBusy(false); }
  };

  const pageAllSelected = rows.length > 0 && selected.size === rows.length;
  const pageSomeSelected = selected.size > 0 && !pageAllSelected;

  return (
    <div data-testid="admin-facilities">
      <h2 className="font-heading text-2xl font-black flex items-center gap-2"><MapPin className="text-[#00E676]" size={22} /> Facilities moderation</h2>
      <Sub>Verify, flag, edit, or delete sport clubs and golf courses worldwide. OSM-imported entries arrive unverified — review before promoting them.</Sub>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="facility-stats">
        {[
          ["Total", stats?.total ?? "—"],
          ["Verified", stats?.verified ?? "—"],
          ["Unverified", stats?.unverified ?? "—"],
          ["Flagged", stats?.flagged ?? "—"],
          ["Claimed (owned)", stats?.claimed ?? "—"],
          ["From OSM", stats?.osm ?? "—"],
          ["Curated seed", stats?.seed ?? "—"],
          ["Manually added", stats?.manual ?? "—"],
        ].map(([label, v]) => (
          <div key={label} className="pp-card p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
            <div className="text-2xl font-black mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 pp-card p-3 flex flex-wrap items-center gap-2">
        <select data-testid="fac-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="pp-input">
          <option value="all">All statuses</option>
          <option value="unverified">Unverified</option>
          <option value="verified">Verified</option>
          <option value="flagged">Flagged</option>
        </select>
        <select data-testid="fac-filter-source" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} className="pp-input">
          <option value="">All sources</option>
          <option value="osm">OpenStreetMap</option>
          <option value="seed">Curated seed</option>
          <option value="manual">Manually added</option>
        </select>
        <input data-testid="fac-filter-country" placeholder="Country (ES, US, …)" maxLength={3}
          value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value.toUpperCase() })} className="pp-input w-32" />
        <input data-testid="fac-filter-q" placeholder="Search by name…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pp-input flex-1 min-w-[200px]" />
        <button onClick={refresh} className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1" data-testid="fac-refresh"><RotateCcw size={12} /> Refresh</button>
        <button onClick={reimportRegions} disabled={importing} className="pp-btn-primary px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-50" data-testid="fac-import-regions">
          <Globe2 size={12} /> {importing ? "Importing…" : "Import worldwide"}
        </button>
      </div>

      <div className="mt-4 pp-card p-3 flex flex-wrap items-center gap-2" data-testid="fac-bulk-bar">
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="fac-select-all-page"
            checked={pageAllSelected}
            ref={(el) => { if (el) el.indeterminate = pageSomeSelected; }}
            onChange={togglePageAll}
            className="h-4 w-4 accent-[#00E676]"
          />
          <span>Select all on this page ({rows.length})</span>
        </label>
        <div className="text-xs text-zinc-500 ms-2" data-testid="fac-selected-count">
          {selected.size === 0 ? "No selection" : `${selected.size} selected`}
        </div>
        <div className="flex-1" />
        <button
          data-testid="fac-bulk-verify"
          onClick={bulkVerifyIds}
          disabled={selected.size === 0 || bulkBusy}
          className="pp-btn-primary px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-40"
        >
          <CheckCircle2 size={12} /> {bulkBusy ? "Verifying…" : `Verify selected (${selected.size})`}
        </button>
        <button
          data-testid="fac-bulk-verify-all"
          onClick={bulkVerifyAllMatching}
          disabled={bulkBusy || total === 0}
          title="Verify every facility matching the current filters, not just this page"
          className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-40"
        >
          <CheckCircle2 size={12} /> {bulkBusy ? "Verifying…" : `Verify all matching (${total})`}
        </button>
      </div>

      <div className="mt-4 pp-card overflow-hidden">
        {loading ? <Skeleton /> : rows.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm" data-testid="fac-empty">No facilities match these filters.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {rows.map((f) => (
              <div key={f.id} className="p-3 flex items-start gap-3 hover:bg-white/[0.02]" data-testid={`fac-row-${f.id}`}>
                <input
                  type="checkbox"
                  checked={selected.has(f.id)}
                  onChange={() => toggleRow(f.id)}
                  data-testid={`fac-select-${f.id}`}
                  className="mt-1.5 h-4 w-4 accent-[#00E676]"
                  aria-label={`Select ${f.name}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold truncate">{f.name}</span>
                    {f.verified && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#00E676]/10 text-[#00E676]">VERIFIED</span>}
                    {f.flagged && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF3B30]/10 text-[#FF3B30]">FLAGGED</span>}
                    {f.access_type && f.access_type !== "unknown" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase">{f.access_type.replace("_", "-")}</span>}
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-500 uppercase">{f.source}</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {[f.city, f.country, (f.sport_slugs || []).join(", "), f.indoor === true ? "indoor" : f.indoor === false ? "outdoor" : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <select data-testid={`fac-access-${f.id}`} value={f.access_type || "unknown"} onChange={(e) => patch(f.id, { access_type: e.target.value }, "Access updated")}
                    className="pp-input text-[10px] py-1 px-1.5 h-auto">
                    <option value="unknown">unknown</option>
                    <option value="public">public</option>
                    <option value="semi_public">semi-public</option>
                    <option value="private">private</option>
                  </select>
                  {!f.verified ? (
                    <IconBtn title="Verify" data-testid={`fac-verify-${f.id}`} onClick={() => patch(f.id, { verified: true, flagged: false }, "Verified")}><CheckCircle2 size={14} /></IconBtn>
                  ) : (
                    <IconBtn title="Un-verify" data-testid={`fac-unverify-${f.id}`} onClick={() => patch(f.id, { verified: false }, "Un-verified")}><CheckCircle2 size={14} /></IconBtn>
                  )}
                  {!f.flagged ? (
                    <IconBtn title="Flag" data-testid={`fac-flag-${f.id}`} onClick={() => patch(f.id, { flagged: true, verified: false }, "Flagged")}><AlertTriangle size={14} /></IconBtn>
                  ) : (
                    <IconBtn title="Clear flag" data-testid={`fac-unflag-${f.id}`} onClick={() => patch(f.id, { flagged: false }, "Flag cleared")}><AlertTriangle size={14} /></IconBtn>
                  )}
                  <IconBtn danger title="Delete" data-testid={`fac-del-${f.id}`} onClick={() => del(f.id, f.name)}><Trash2 size={14} /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
